import { EventEmitter, Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { take } from 'rxjs/operators';
import { Category } from '../model/Category';
import { Config } from '../model/Config';
import { Customer, CustomerAdapter } from '../model/Customer';
import { Organisation, OrganisationAdapter } from '../model/Organisation';
import { SaleChannel, SaleChannelAdapter } from '../model/SaleChannel';
import { Transaction, TransactionAdapter } from '../model/Transaction';
import { User, UserAdapter } from '../model/User';
import { ApiService } from './api.service';

@Injectable({
  providedIn: 'root',
})
export class SharedVariablesService extends ApiService {
  private _transaction: Transaction;
  private _saleChannel: SaleChannel;
  private _organisation: Organisation;
  private _customer: Customer|User;
  private _user: Customer|User;
  private _singleOrganisation: boolean = true;
  private readonly _instance: string;
  public saleChannelChanged: Subject<SaleChannel> = new Subject<SaleChannel>();
  public rootCategory: Category;
  public updatedTransaction: EventEmitter<Transaction> = new EventEmitter<Transaction>();
  public config: Config = new Config();
  public showCartInPage: boolean = false;
  public cartInPageVisible: boolean = false;
  public googleAnalyticsConsent: boolean = false;
  public _env: string = 'PROD';

  constructor(private transactionAdapter: TransactionAdapter,
              private saleChannelAdapter: SaleChannelAdapter,
              private organisationAdapter: OrganisationAdapter,
              private customerAdapter: CustomerAdapter,
              private userAdapter: UserAdapter) {
    super();
    // Load from local storage
    if (window.localStorage.getItem('organisation') !== null) {
      this.organisation = this.organisationAdapter.adapt(JSON.parse(window.localStorage.getItem('organisation')));
    }
    if (window.localStorage.getItem('saleChannel') !== null) {
      this.saleChannel = this.saleChannelAdapter.adapt(JSON.parse(window.localStorage.getItem('saleChannel')));
    }
    // Load from session
    if (window.sessionStorage.getItem('transaction') !== null) {
      this.transaction = this.transactionAdapter.adapt(JSON.parse(window.sessionStorage.getItem('transaction')));
    }
    if (window.sessionStorage.getItem('customer') !== null) {
      this.customer = this.customerAdapter.adapt(JSON.parse(window.sessionStorage.getItem('customer')));
    }
    if (window.sessionStorage.getItem('user') !== null) {
      this.user = this.userAdapter.adapt(JSON.parse(window.sessionStorage.getItem('user')));
    }
    if (window.sessionStorage.getItem('singleOrganisation') !== null) {
      this._singleOrganisation = JSON.parse(window.sessionStorage.getItem('singleOrganisation'));
    }

    this._instance = this.config['INSTANCE_ID'];
    this.getEnv();
  }

  clear() {
    this._transaction = undefined;
    this.updatedTransaction.emit(undefined);
    window.sessionStorage.removeItem('transaction');
  }

  clearLoginInfo() {
    this.customer = undefined;
    this.user = undefined;
    window.sessionStorage.removeItem('customer');
    window.sessionStorage.removeItem('user');
  }

  get transaction(): Transaction {
    return this._transaction;
  }

  set transaction(value: Transaction) {
    this._transaction = value;
    if (value !== undefined) {
      window.sessionStorage.setItem('transaction', JSON.stringify(this._transaction.prepareForJSON()));
    } else {
      window.sessionStorage.removeItem('transaction');
    }
    this.updatedTransaction.emit(value);
  }

  get organisation(): Organisation {
    return this._organisation;
  }

  set organisation(value: Organisation) {
    this._organisation = value;
    if (value !== undefined) {
      window.localStorage.setItem('organisation', JSON.stringify(this.organisationAdapter.prepare(this.organisation)));
    } else {
      window.localStorage.removeItem('organisation');
    }
  }

  get customer(): Customer {
    return this._customer as Customer;
  }

  set customer(value: Customer) {
    this._customer = value;
    if (value !== undefined) {
      window.sessionStorage.setItem('customer', JSON.stringify(this.customerAdapter.prepare(this.customer)));
    } else {
      window.sessionStorage.removeItem('customer');
    }
  }

  get user(): User {
    return this._user as User;
  }

  set user(value: User) {
    this._user = value;
    if (value !== undefined) {
      window.sessionStorage.setItem('user', JSON.stringify(this.user));
    } else {
      window.sessionStorage.removeItem('user');
    }
  }

  get saleChannel(): SaleChannel {
    return this._saleChannel;
  }

  set saleChannel(value: SaleChannel) {
    this._saleChannel = value;
    if (value !== undefined) {
      window.localStorage.setItem('saleChannel', JSON.stringify(this.saleChannelAdapter.prepare(this._saleChannel)));
      // We update config
      this.config.overrideWithSaleChannelProperties(value.properties);
      window.setTimeout(() => {
        this.saleChannelChanged.next(this.saleChannel);
      }, 2000);
    } else {
      this.rootCategory = undefined;
      window.localStorage.removeItem('saleChannel');
    }
  }

  get singleOrganisation(): boolean {
    return this._singleOrganisation;
  }

  set singleOrganisation(value: boolean) {
    this._singleOrganisation = value;
    window.sessionStorage.setItem('singleOrganisation', JSON.stringify(this._singleOrganisation));
  }

  get instance(): string {
    return this._instance;
  }

  /**
   * Klaro consent watcher
   * @param obj
   * @param name
   * @param data
   */
  update(obj, name, data) {
    this.googleAnalyticsConsent = obj.consents['google-analytics'];
  }

  get env(): string {
    return this._env;
  }

  set env(value: string) {
    this._env = value;
  }

  getEnv() {
    let currentURI: any = window.location.host;
    currentURI = currentURI.split('.');
    if (currentURI.includes('staging') || currentURI.includes('testing') || currentURI.includes('localhost:4200')) {
      this._env = undefined;
    } else {
      this._env = 'PROD';
    }
  }
}
